import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, ShieldCheck, ArrowLeft, Crown, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const NSB_MEMBERSHIP_URL = "https://scoutbureauos.polsia.app/membership";

/**
 * SSO Launch Pad — bridges NSB sessions into partner platforms.
 *
 * For now this is a placeholder that opens the partner site in a new tab.
 * Future: NSB Central Auth Hub will issue a signed JWT scoped to the partner,
 * append it as `?nsb_token=...`, and partners will verify it on landing.
 */
const PARTNERS = {
  "6tool":            { name: "Baseball IQ — 6 Tool",          logo: "/partner-logos/6tool.png",          url: "https://6tool.com",                    color: "bg-[#172533]", accent: "text-yellow-400" },
  "gamesense":        { name: "GameSense",                      logo: "/partner-logos/gamesense.png",      url: "https://gamesensesports.com",          color: "bg-black",     accent: "text-teal-400" },
  "demotu":           { name: "Demotu",                          logo: "/partner-logos/demotu.png",         url: "https://demotu.com",                   color: "bg-zinc-900",  accent: "text-white" },
  "sage-scholars":    { name: "SAGE Scholars Tuition Rewards",  logo: "/partner-logos/sage-scholars.jpg",  url: "https://www.tuitionrewards.com",       color: "bg-white",     accent: "text-blue-700" },
  "quality-at-bats":  { name: "Quality At-Bats — Steve Springer", logo: "/partner-logos/quality-at-bats.png", url: "https://qualityatbats.com",         color: "bg-black",     accent: "text-white" },
  "college-ed-insights": { name: "CollegeEdInsights",              logo: "/partner-logos/college-ed-insights.svg", url: "https://collegeedinsights.com",  color: "bg-slate-900", accent: "text-sky-400" },
};

export default function SSOLaunchPage() {
  const { partner } = useParams();
  const p = PARTNERS[partner];
  const [countdown, setCountdown] = useState(3);
  const [sso, setSso] = useState(null); // { token, tier, paid, sport }
  const [redirectUrl, setRedirectUrl] = useState(p?.url || "");

  // Fetch signed SSO token from NSB backend
  useEffect(() => {
    if (!p) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const r = await fetch(`${API}/api/sso/launch/${encodeURIComponent(partner)}`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        const txt = await r.text();
        let data = {};
        try { data = JSON.parse(txt); } catch {}
        if (!cancelled && r.ok && data.token) {
          setSso(data);
          const sep = p.url.includes("?") ? "&" : "?";
          setRedirectUrl(`${p.url}${sep}nsb_token=${encodeURIComponent(data.token)}`);
        }
      } catch (_e) { /* fallback to plain redirect */ }
    })();
    return () => { cancelled = true; };
  }, [partner, p]);

  useEffect(() => {
    if (!p) return;
    if (sso === null) return;             // wait until tier known
    if (!sso.paid) return;                // freemium → require manual click
    if (countdown <= 0) {
      window.location.href = redirectUrl;
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, p, redirectUrl, sso]);

  if (!p) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900">Unknown partner</h1>
          <p className="text-gray-500 mt-2">No SSO target configured for "{partner}".</p>
          <Link to="/dashboard"><Button className="mt-4">Back to Dashboard</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${p.color} flex flex-col items-center justify-center px-4 relative`}>
      <Link to="/dashboard" className="absolute top-6 left-6 inline-flex items-center text-sm text-white/60 hover:text-white" data-testid="back-from-sso">
        <ArrowLeft size={14} className="mr-1.5" /> Cancel
      </Link>

      <div className="text-center max-w-lg" data-testid={`sso-launch-${partner}`}>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/80 text-[10px] uppercase tracking-[0.25em] font-bold mb-6">
          <ShieldCheck size={12} /> NSB Single Sign-On
        </div>
        <div className="bg-white rounded-2xl p-8 mb-6 inline-block shadow-2xl">
          <img src={p.logo} alt={p.name} className="h-20 w-auto object-contain" data-testid="sso-partner-logo" />
        </div>
        <h1 className={`text-2xl sm:text-3xl font-black ${p.accent} tracking-tight`}>{p.name}</h1>
        <p className="text-white/70 mt-3">Connecting your NSB session…</p>

        {sso && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/15 text-white/80 text-xs" data-testid="sso-tier-badge">
            {sso.paid ? <Crown size={12} className="text-amber-400" /> : <ShieldCheck size={12} className="text-white/60" />}
            <span className="font-semibold capitalize">{sso.tier} tier</span>
            <span className="text-white/40">·</span>
            <span className="text-white/60 capitalize">{sso.sport}</span>
            {sso.paid && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px] font-bold uppercase tracking-wider">Full Access</span>}
          </div>
        )}

        {/* Tier-aware action area */}
        {sso === null ? (
          /* Loading state while we fetch tier */
          <div className="mt-8">
            <p className="text-xs text-white/40 uppercase tracking-widest">Verifying NSB session…</p>
          </div>
        ) : sso.paid ? (
          /* Paid tier → fast auto-redirect (existing flow) */
          <>
            <div className="mt-8">
              <div className="text-6xl font-black text-white mb-2" data-testid="sso-countdown">{countdown > 0 ? countdown : "→"}</div>
              <p className="text-xs text-white/40 uppercase tracking-widest">
                {countdown > 0 ? "Launching automatically" : "Redirecting…"}
              </p>
            </div>
            <div className="mt-8 flex justify-center">
              <a href={redirectUrl} target="_self" rel="noopener noreferrer">
                <Button className="bg-white text-gray-900 hover:bg-gray-100 font-bold" data-testid="sso-launch-now-btn">
                  Launch Now <ExternalLink size={14} className="ml-1.5" />
                </Button>
              </a>
            </div>
          </>
        ) : (
          /* Freemium → upgrade gate */
          <div className="mt-8 max-w-md mx-auto bg-white/[0.04] border border-white/15 rounded-2xl p-6 text-left backdrop-blur" data-testid="sso-upgrade-gate">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Crown size={16} className="text-black" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-amber-400 font-bold">Limited Access</div>
                <div className="text-sm font-bold text-white">You're on NSB Freemium</div>
              </div>
            </div>
            <p className="text-sm text-white/70 leading-relaxed mb-4">
              You can still launch <strong className="text-white">{p.name}</strong> below, but you'll see their free tier only. <strong className="text-amber-300">Upgrade to NSB Plus</strong> and a single click unlocks full access to <strong>{p.name}</strong> — plus GameSense, 6Tool, Demotu, Quality At-Bats, and SAGE Scholars Rewards.
            </p>

            <div className="space-y-2 mb-5">
              <UnlockItem label="Full CollegeEdInsights & HBCU Insights access" />
              <UnlockItem label="GameSense pitch recognition platform" />
              <UnlockItem label="6 Tool baseball IQ training" />
              <UnlockItem label="Demotu FMS + Speed/Strength testing" />
              <UnlockItem label="SAGE Scholars Tuition Rewards (400+ colleges)" />
            </div>

            <a href={NSB_MEMBERSHIP_URL} target="_blank" rel="noopener noreferrer" className="block">
              <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-black shadow-[0_0_30px_rgba(245,158,11,0.3)]" data-testid="sso-upgrade-btn">
                <Crown size={14} className="mr-1.5" /> Upgrade to NSB Plus
              </Button>
            </a>

            <div className="text-center mt-4">
              <a href={redirectUrl} className="text-xs text-white/40 hover:text-white/70 transition inline-flex items-center gap-1" data-testid="sso-continue-freemium">
                Continue to {p.name} on free tier <ExternalLink size={10} />
              </a>
            </div>
          </div>
        )}

        <p className="text-[10px] text-white/30 mt-8 max-w-sm mx-auto">
          You will be signed in automatically via NSB SSO. If the partner site doesn't recognize your account, contact NSB support.
        </p>
      </div>
    </div>
  );
}

function UnlockItem({ label }) {
  return (
    <div className="flex items-center gap-2 text-xs text-white/80">
      <Sparkles size={11} className="text-amber-400 flex-shrink-0" />
      <span>{label}</span>
    </div>
  );
}
